import { useCleanPostData } from '../contexts/CleanPostDataContext'
import tw, { css } from 'twin.macro'
import Button, { ButtonSize, ButtonVariant } from '@components/DesignSystem/Button'
import { useTranslation } from '@i18n'
import { PlusIcon, CheckIcon } from '@components/Icons'
import { useSelector, useDispatch } from 'react-redux'
import { postUserFollow } from '@app-core/post/actions'
import { dispatchCustomEvent, FollowUIEvents } from '@libs/customEvents'
import { FollowActionType } from '@libs/eventTracking/article'
import { TrackingSection } from '../contexts/TrackingSectionContext'
import { useNormalizedPublishedTime } from '@libs/getNormalizedPublishedTime'
import LoginAlert from '@components/Login/LoginAlert'
import { checkIsGuest } from '@libs/auth-utils'
import { useState } from 'react'
import PopLink from '@components/PopLink'
import { publicUrl } from '@libs/appUrl'

const publishedTimeStyle = css`
  font-size: 12px;
  line-height: 130%;
  letter-spacing: 0.4px;
`

function ArticleUserInfo() {
  const { isFollowingAuthor, userProfile, post } = useCleanPostData()
  const isSecret = post.clas.includes('secret')

  const info = useMiscInfo()

  return (
    <div className="article-user-info" tw="flex flex-row items-center justify-between">
      <div tw="flex flex-row items-center space-x-3">
        <PopLink
          url={publicUrl(userProfile.link)}
          href={userProfile.link}
          tw="h-10 w-10 rounded-full overflow-hidden hover:opacity-60"
          disableOnClick={isSecret}
        >
          <img src={userProfile.avatarUrl} alt={userProfile.name} />
        </PopLink>
        <div tw="flex flex-col space-y-1">
          <PopLink
            href={userProfile.link}
            url={publicUrl(userProfile.link)}
            tw="text-black text-b1 leading-h4 font-medium"
            disableOnClick={isSecret}
          >
            {userProfile.name}
          </PopLink>
          <span css={[tw`text-gray-600`, publishedTimeStyle]}>{info}</span>
        </div>
      </div>
      {isFollowingAuthor ? <FollowingButton /> : <FollowButton />}
    </div>
  )
}

export default ArticleUserInfo

function useMiscInfo() {
  const {
    location,
    userProfile,
    post: { publishedTime: publishedTimeInput },
  } = useCleanPostData()

  const publishedTime = useNormalizedPublishedTime(publishedTimeInput)
  const hasLocationInfo = Boolean(location)

  let info = null

  if (publishedTime && hasLocationInfo) {
    info = (
      <>
        <PublishedTime href={userProfile.link} {...publishedTime} />
        <span className="separator">・</span>
        <Location {...location} />
      </>
    )
  } else if (hasLocationInfo) {
    info = <Location {...location} />
  } else if (publishedTime) {
    info = <PublishedTime href={userProfile.link} {...publishedTime} />
  }

  return info
}

function PublishedTime({ dateTime = undefined, formatted, href }) {
  return (
    <a href={href}>
      <time dateTime={dateTime}>{formatted}</time>
    </a>
  )
}

function Location({ id, name }) {
  return (
    <a key="location" href={`/${id.replace('.', '/')}`}>
      {name}
    </a>
  )
}

function FollowButton() {
  const { t } = useTranslation('post')
  const {
    post: { userInfo },
  } = useCleanPostData()
  const dispatch = useDispatch()
  const { openLoginAlert, loginAlert } = useLoginAlert()
  const isGuest = useCheckGuest()

  const follow = () => {
    dispatchCustomEvent(FollowUIEvents.CLICK_FOLLOW, {
      actionType: FollowActionType.FOLLOW,
      section: TrackingSection.ARTICLE_INFO,
    })

    if (isGuest) return openLoginAlert()

    dispatch(postUserFollow({ followID: userInfo.mid, follow: 1 }))
  }

  return (
    <>
      <Button onClick={follow} size={ButtonSize.medium} variant={ButtonVariant.primary}>
        <PlusIcon />
        <span>{t('follow')}</span>
      </Button>
      {loginAlert}
    </>
  )
}

function FollowingButton() {
  const { t } = useTranslation('post')
  const {
    post: { userInfo },
  } = useCleanPostData()
  const dispatch = useDispatch()

  const unfollow = () => {
    dispatchCustomEvent(FollowUIEvents.CLICK_FOLLOW, {
      actionType: FollowActionType.UNFOLLOW,
      section: TrackingSection.ARTICLE_INFO,
    })
    dispatch(postUserFollow({ followID: userInfo.mid, follow: 0 }))
  }

  return (
    <Button onClick={unfollow} size={ButtonSize.medium} variant={ButtonVariant.outlinePrimary}>
      <CheckIcon />
      <span>{t('following')}</span>
    </Button>
  )
}

// TODO: 獨立這兩個 hooks
const useLoginAlert = () => {
  const [isOpen, setIsOpen] = useState(false)

  const loginAlert = <LoginAlert target="_self" isOpen={isOpen} onClose={() => setIsOpen(false)} />
  const openLoginAlert = () => setIsOpen(true)

  return { loginAlert, openLoginAlert }
}

const useCheckGuest = (): boolean => {
  return useSelector<any, boolean>((state) => {
    return checkIsGuest(state.currentUser.getIn(['userInfo', 'type']))
  })
}
